/**
 * `useUsage` — workspace-level usage + plan limits.
 *
 * Fetches `GET /v1/usage`. Surfaces MTD on-demand count, monitor
 * counts, member counts, and the plan limits alongside each.
 *
 * Query key includes the active JWT-claim org id (via `useActiveOrg`)
 * so the cache drops cleanly on org-switch — the per-org data is
 * disjoint and we do NOT want to flash the previous org's usage
 * while the new one is loading. Keyed by the JWT-claim org (NOT
 * the live SDK org) because every API request signs with the JWT,
 * so the server response is authoritative for whichever org the
 * claim resolves to — using the claim as the cache key guarantees
 * "what we render" matches "what we fetched".
 */
import { useAuth } from '@clerk/react';
import type { QueryClient } from '@tanstack/react-query';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api';
import type { UsageResponse } from '../api-types';
import { useActiveOrg } from './active-org';
import { usageKeys } from './query-keys';

export function useUsage(): UseQueryResult<UsageResponse> {
  const { isSignedIn, isLoaded } = useAuth();
  const { orgId } = useActiveOrg();
  return useQuery({
    queryKey: usageKeys.byOrg(orgId),
    queryFn: () => apiFetch<UsageResponse>('/v1/usage'),
    enabled: isLoaded && !!isSignedIn && !!orgId,
  });
}

export function invalidateUsage(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: usageKeys.all });
}
